import type { RawVersionMetadata } from "./types";
import DesignStateService from "../../service/DesignStateService";
import SpecsRevisionService from "../../service/SpecsRevisionService";
import { getAssetPreviewValue } from "./specsAssetUrls";
import {
  upsertProductSnapshotIntoCatalog,
  type SpecsCatalog,
} from "./specsModel/catalog";
import { upsertSpecsSnapshotIntoCatalog } from "./specsModel/specsLib";
import type { SpecsModelSnapshot } from "./specsModel/types";

export type StudioLabelKeys = {
  elements: Record<string, string>;
  groups: Record<string, string>;
};

export function uniqueStrings(
  values: Array<string | null | undefined>
): string[] {
  return Array.from(new Set(values.filter(Boolean) as string[])).sort();
}

export function getStudioLabelKeys(
  rawVersionMetadata: RawVersionMetadata | null
): StudioLabelKeys {
  if (!rawVersionMetadata?.studioLabelKeys) {
    return {
      elements: {},
      groups: {},
    };
  }

  return rawVersionMetadata.studioLabelKeys as StudioLabelKeys;
}

const DEFAULT_POSITION_SIDE_FALLBACK_SIZE = 100;

export function loadImageDimensions(url: string | null): Promise<{
  width: number;
  height: number;
} | null> {
  if (!url) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const image = new Image();

    image.onload = () => {
      resolve({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      });
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

export function createDefaultPositionSideForImage({
  sideName,
  imageSize,
}: {
  sideName: string;
  imageSize: { width: number; height: number } | null;
}) {
  const imageWidth =
    imageSize?.width ?? DEFAULT_POSITION_SIDE_FALLBACK_SIZE * 2;
  const imageHeight =
    imageSize?.height ?? DEFAULT_POSITION_SIDE_FALLBACK_SIZE * 2;
  const size = Math.max(1, Math.round(Math.min(imageWidth, imageHeight) / 2));

  return {
    name: sideName,
    zIndex: 15000,
    x: Math.round((imageWidth - size) / 2),
    y: Math.round((imageHeight - size) / 2),
    w: size,
    h: size,
    rotation: 0,
    alignmentX: "c",
    alignmentY: "t",
    planeAngle: 0,
  };
}

export function createEmptyPosition({
  side,
}: {
  side?: ReturnType<typeof createDefaultPositionSideForImage>;
} = {}) {
  const stamp = Date.now();
  return {
    id: `PL-NEW-${stamp}`,
    allowedDecorationTypes: [],
    deprecatedDecorationTypes: [],
    designState: {
      positionName: `New position ${stamp}`,
      groupName: "",
      minWidthInches: 0,
      maxWidthInches: 0,
      minHeightInches: 0,
      maxHeightInches: 0,
      increment: 0.25,
      planeAngle: 0,
      dropdownLabel: "",
    },
    generator: {
      boxWidthInches: 0,
      boxHeightInches: 0,
      sides: [
        side ??
          createDefaultPositionSideForImage({
            sideName: "front",
            imageSize: null,
          }),
      ],
    },
    techpack: {
      position: `PL-NEW-${stamp}`,
      alignmentX: "c",
      alignmentY: "t",
      patternUrl: "",
      conditionalPattern: [],
      assetFilenamePrefix: "",
    },
  };
}

function canPreviewImageUrl(value?: string | null) {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  return (
    trimmed.startsWith("data:image/") ||
    /\.(png|jpe?g|webp|gif|svg|bmp|tiff?)(\?.*)?$/i.test(trimmed)
  );
}

export async function loadSpecsEditorImportSpecs() {
  const [publishedResponse, draftProductResponse, draftNonProductResponse] =
    await Promise.all([
      SpecsRevisionService.getPublishedSpecsCatalog(),
      SpecsRevisionService.getDraftProductSnapshots(),
      SpecsRevisionService.getDraftNonProductSnapshots().catch(() => ({
        data: {
          snapshots: {
            families: [],
            fabrics: [],
            decorations: [],
          },
        },
      })),
    ]);

  const draftProductSnapshots =
    draftProductResponse.data.snapshots.filter(isProductSnapshot);
  const catalogWithDraftFamilies =
    draftNonProductResponse.data.snapshots.families
      .filter(isNamedSpecSnapshot)
      .reduce<SpecsCatalog>(
        (catalog, snapshot) =>
          upsertSpecsSnapshotIntoCatalog(catalog, "family", snapshot),
        publishedResponse.data as SpecsCatalog
      );
  const catalogWithDraftFabrics = draftNonProductResponse.data.snapshots.fabrics
    .filter(isNamedSpecSnapshot)
    .reduce<SpecsCatalog>(
      (catalog, snapshot) =>
        upsertSpecsSnapshotIntoCatalog(catalog, "fabric", snapshot),
      catalogWithDraftFamilies
    );
  const catalogWithDraftNonProducts =
    draftNonProductResponse.data.snapshots.decorations
      .filter(isDecorationSnapshot)
      .reduce<SpecsCatalog>(
        (catalog, snapshot) =>
          upsertSpecsSnapshotIntoCatalog(catalog, "decoration", snapshot),
        catalogWithDraftFabrics
      );

  return draftProductSnapshots.reduce<SpecsCatalog>(
    (catalog: SpecsCatalog, snapshot: SpecsModelSnapshot) => {
      return upsertProductSnapshotIntoCatalog(catalog, snapshot);
    },
    catalogWithDraftNonProducts
  );
}

function isProductSnapshot(
  snapshot: Record<string, unknown> | null | undefined
): snapshot is SpecsModelSnapshot {
  if (!snapshot || typeof snapshot !== "object") {
    return false;
  }

  return typeof snapshot.name === "string" && Array.isArray(snapshot.versions);
}

function isNamedSpecSnapshot(
  snapshot: Record<string, unknown> | null | undefined
): snapshot is Record<string, unknown> {
  if (!snapshot || typeof snapshot !== "object") {
    return false;
  }

  return typeof snapshot.name === "string";
}

function isDecorationSnapshot(
  snapshot: Record<string, unknown> | null | undefined
): snapshot is Record<string, unknown> {
  if (!snapshot || typeof snapshot !== "object") {
    return false;
  }

  return typeof snapshot.id === "string";
}

export function resolveImportedVersionName(
  specs: any,
  productName: string,
  preferredVersion: string
) {
  const versions =
    specs?.products?.find((product: any) => product?.name === productName)
      ?.versions ?? [];

  if (versions.some((version: any) => version?.name === preferredVersion)) {
    return preferredVersion;
  }

  return versions[versions.length - 1]?.name ?? preferredVersion;
}

export function getAssetPreviewUrl(
  source: Record<string, any> | null | undefined,
  key: string,
  fallback?: string | null
) {
  return getAssetPreviewValue(source, key, fallback);
}

export { canPreviewImageUrl };
